import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import type { CustomerOrder, StaffOrderPaymentMethod, Sale } from "../../api/types";
import { EmptyState } from "../ui/EmptyState";
import { Modal } from "../Modal";
import { ReasonModal } from "../ui/ReasonModal";
import { RecordPaymentModal } from "./RecordPaymentModal";
import { RefundSaleModal } from "../sale/RefundSaleModal";
import { useEffect } from "react";
import { useAuth } from "../../state/AuthContext";
import { orderNeedsCourier } from "./orderUi";
import { useBoxPickPrompt } from "../shop-floor/useBoxPickPrompt";

/**
 * G-16: the ONE action row an order carries everywhere — extracted from
 * DayOrdersModal so the detail modal cannot drift from the day view
 * (transition buttons, dispatch links, Ring up / Link sale, the OD-30
 * Record-payment door, cancel). Owns its own busy state and modals.
 */
export function OrderActions({
  order,
  onChanged,
  onError,
}: {
  order: CustomerOrder;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const { hasRole } = useAuth();

  const run = async (action: () => Promise<CustomerOrder>) => {
    setBusy(true);
    onError(null);
    try {
      await action();
      await onChanged();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Could not update the order.");
    } finally {
      setBusy(false);
    }
  };

  // D-11: kit ReasonModal instead of the native browser dialog.
  const [cancelOpen, setCancelOpen] = useState(false);
  // G-3: link an existing register sale to this order (→ COMPLETED).
  const [linkSaleOpen, setLinkSaleOpen] = useState(false);
  // OD-30: the buyer's money door — couriers never take payment.
  const [paymentOpen, setPaymentOpen] = useState(false);
  // OD-32: "the customer collected this — record it" (office twin of the
  // kiosk Collect; READY pickups only, the one completion engine).
  const [handoverOpen, setHandoverOpen] = useState(false);
  // G-162 (owner 2026-10-19): goods money on a handed-over order goes back
  // through its sale, in the refund window.
  const [refundSale, setRefundSale] = useState<Sale | null>(null);
  const canRefundGoods =
    hasRole("OWNER", "MANAGER") &&
    order.status === "COMPLETED" &&
    order.sale !== null &&
    order.shopifyOrder === null &&
    Number(order.sale.refundableEur) > 0;
  const openRefund = async () => {
    if (!order.sale) return;
    onError(null);
    try {
      setRefundSale(await api.getSale(order.sale.id));
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Could not open the sale.");
    }
  };

  const cancel = async (reason: string | undefined) => {
    setCancelOpen(false);
    await run(() => api.cancelCustomerOrder(order.id, { reason }));
  };

  return (
    <>
      <div className="button-row">
        {hasRole("OWNER", "MANAGER") &&
        (order.status === "NEW" || order.status === "CONFIRMED") ? (
          <Link to={`/app/orders/build/${encodeURIComponent(order.id)}`} className="button tinted">
            Edit order
          </Link>
        ) : null}
        {order.status === "NEW" ? (
          <button
            type="button"
            className="button"
            onClick={() => void run(() => api.confirmCustomerOrder(order.id))}
            disabled={busy}
          >
            Confirm
          </button>
        ) : null}
        {order.status === "CONFIRMED" ? (
          <button
            type="button"
            className="button"
            onClick={() => void run(() => api.startCustomerOrderPreparation(order.id))}
            disabled={busy}
          >
            Start preparation
          </button>
        ) : null}
        {order.status === "IN_PREPARATION" ? (
          <button
            type="button"
            className="button"
            onClick={() => void run(() => api.markCustomerOrderReady(order.id))}
            disabled={busy}
          >
            Ready
          </button>
        ) : null}
        {/* G-166: a handed-over web order still needs its courier job. */}
        {(order.fulfillment === "COURIER" ||
          order.fulfillment === "EXTERNAL_COURIER") &&
        !order.deliveryJob &&
        (order.status !== "COMPLETED" || order.webAwaitingCourier) &&
        order.status !== "CANCELLED" ? (
          <Link
            to={`/app/dispatch?orderId=${encodeURIComponent(order.id)}`}
            className="button plain"
          >
            Dispatch delivery
          </Link>
        ) : null}
        {order.deliveryJob ? (
          <Link
            to="/app/dispatch"
            className={orderNeedsCourier(order) ? "button" : "button plain"}
          >
            {orderNeedsCourier(order)
              ? `Assign courier · ${order.deliveryJob.code}`
              : `Delivery ${order.deliveryJob.code}`}
          </Link>
        ) : null}
        {/* G-166 (owner 2026-10-20): a web order leaves through "Hand to
            delivery" on the web orders list only — the server refuses these
            three doors for it, so they are not offered. */}
        {/* G-177 (OD-299): also a delivered order whose sale was not written. */}
        {hasRole("OWNER", "MANAGER") &&
        ((order.status === "READY" && order.fulfillment === "PICKUP") ||
          order.deliveredNotCompleted) &&
        !order.webAwaitsHandover ? (
          <button
            type="button"
            className="button"
            onClick={() => setHandoverOpen(true)}
            disabled={busy}
          >
            Record handover
          </button>
        ) : null}
        {/* OD-32 review fix: once the bouquet is BUILT, no linkable sale can
            exist (the OD-31 guard refuses ringing it up walk-in and the
            containment gate refuses a sale without it) — Record handover is
            that order's door, so the dead buttons hide. */}
        {/* G-141: owner and manager only — the server's ring-up and link-sale
            are theirs, so a florist's tap used to leave a sale with no order. */}
        {hasRole("OWNER", "MANAGER") &&
        ["CONFIRMED", "IN_PREPARATION", "READY"].includes(order.status) &&
        !order.bouquetBuild &&
        !order.webAwaitsHandover ? (
          <>
            <Link
              to={`/app/sales/new?orderId=${order.id}`}
              className="button tinted"
            >
              Ring up
            </Link>
            <button
              type="button"
              className="button plain"
              onClick={() => setLinkSaleOpen(true)}
              disabled={busy}
            >
              Link sale
            </button>
          </>
        ) : null}
        {/* OD-30: money can arrive before, at, or long after the goods —
            including on a completed delivery whose buyer still owes. */}
        <button
          type="button"
          className={
            order.balanceDueEur && Number(order.balanceDueEur) > 0
              ? "button tinted"
              : "button plain"
          }
          onClick={() => setPaymentOpen(true)}
          disabled={busy}
        >
          {order.balanceDueEur && Number(order.balanceDueEur) > 0
            ? `Record payment · ${order.balanceDueEur} € due`
            : "Record payment"}
        </button>
        {canRefundGoods ? (
          <button type="button" className="button plain" onClick={() => void openRefund()} disabled={busy}>
            Refund
          </button>
        ) : null}
        {order.status !== "COMPLETED" && order.status !== "CANCELLED" ? (
          <button
            type="button"
            className="button danger"
            onClick={() => setCancelOpen(true)}
            disabled={busy}
          >
            Cancel order
          </button>
        ) : null}
      </div>

      {paymentOpen ? (
        <RecordPaymentModal
          order={order}
          onRefundGoods={
            canRefundGoods
              ? () => {
                  setPaymentOpen(false);
                  void openRefund();
                }
              : undefined
          }
          onClose={() => setPaymentOpen(false)}
          onSaved={async () => {
            setPaymentOpen(false);
            await onChanged();
          }}
        />
      ) : null}

      {refundSale ? (
        <RefundSaleModal
          sale={refundSale}
          onClose={() => setRefundSale(null)}
          onRefunded={async () => {
            setRefundSale(null);
            await onChanged();
          }}
        />
      ) : null}

      {linkSaleOpen ? (
        <LinkSaleModal
          order={order}
          onClose={() => setLinkSaleOpen(false)}
          onLinked={async () => {
            setLinkSaleOpen(false);
            await onChanged();
          }}
        />
      ) : null}

      {handoverOpen ? (
        <RecordHandoverModal
          order={order}
          onClose={() => setHandoverOpen(false)}
          onSaved={async () => {
            setHandoverOpen(false);
            await onChanged();
          }}
        />
      ) : null}

      {cancelOpen ? (
        <ReasonModal
          title={`Cancel order ${order.code}?`}
          message="The order is marked cancelled; the reason is kept on the record."
          reasonMode="optional"
          confirmLabel="Cancel order"
          cancelLabel="Keep order"
          destructive
          busy={busy}
          onConfirm={(reason) => void cancel(reason)}
          onClose={() => setCancelOpen(false)}
        />
      ) : null}
    </>
  );
}

/**
 * OD-32: the office handover — completes a READY pickup order through the
 * one completion engine (the sale it creates CONTAINS the order's bouquet by
 * construction). The price charged prefills with the agreed figure; changing
 * it is normal business — recorded and flagged, never blocked.
 */
function RecordHandoverModal({
  order,
  onClose,
  onSaved,
}: {
  order: CustomerOrder;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [priceStr, setPriceStr] = useState(order.totalEur ?? "");
  const [method, setMethod] = useState<StaffOrderPaymentMethod>("CARD");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // G-112 (OD-103): the handover sells the shop items — one still in a
  // storage box (and not answered when it was picked) asks here.
  const boxPrompt = useBoxPickPrompt();

  // OD-32 review fix: a changed price is absorbed by the BOUQUET line, so
  // the field is editable only when the order has a built bouquet — an
  // item-only order was being invited to enter a price the server refuses.
  const priceEditable = order.bouquetBuild != null;
  const agreed = order.totalEur != null ? Number(order.totalEur) : null;
  const charged = priceEditable ? Number(priceStr) : (agreed ?? NaN);
  const priceValid = priceEditable
    ? priceStr.trim() !== "" && Number.isFinite(charged) && charged >= 0
    : agreed !== null;
  const differs =
    priceEditable && priceValid && (agreed === null || charged !== agreed);
  // What is owed is the price charged plus the courier fee (OD-100) — the
  // server's rule; a delivered order (G-177, OD-299) carries its fee.
  const owed = charged + Number(order.deliveryFeeEur ?? 0);
  const dueNow = priceValid
    ? Math.max(0, owed - Number(order.paidEur))
    : null;
  const refundDue =
    priceValid && owed < Number(order.paidEur)
      ? Number(order.paidEur) - owed
      : 0;

  const submit = async () => {
    if (!priceValid) {
      setError(
        priceEditable
          ? "Enter the price charged."
          : "This order has no agreed price — set it on the order first.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await boxPrompt.run((boxAnswers) =>
        api.recordOrderHandover(order.id, {
          method,
          ...(differs ? { chargedTotalEur: charged } : {}),
          ...(boxAnswers.length ? { boxAnswers } : {}),
        }),
      );
      await onSaved();
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Could not record the handover.",
      );
      setBusy(false);
    }
  };

  return (
    <Modal title={`Record handover — ${order.code}`} onClose={onClose}>
      {boxPrompt.element}
      {order.deliveredNotCompleted ? (
        <span className="badge danger">Delivered · not completed</span>
      ) : (
        <p className="muted">
          The customer collected this order. The sale is recorded at the price
          below — including the bouquet — and the order completes.
        </p>
      )}
      {error ? <div className="alert error">{error}</div> : null}
      {priceEditable ? (
        <div className="field">
          <label htmlFor="handover-price">Price charged (€)</label>
          <input
            id="handover-price"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={priceStr}
            onChange={(e) => setPriceStr(e.target.value)}
            disabled={busy}
          />
          {differs && agreed !== null ? (
            <small className="hint">
              Differs from the agreed {agreed.toFixed(2)} € — recorded on the
              order&apos;s timeline.
            </small>
          ) : null}
        </div>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          {agreed !== null
            ? `Charged at the agreed ${agreed.toFixed(2)} € — item-only orders change price by editing their lines.`
            : "This order has no agreed price — set it on the order before recording the handover."}
        </p>
      )}
      <div className="field">
        <label>Remaining balance paid by</label>
        <div className="button-row">
          {(["CASH", "CARD", "BANK_TRANSFER"] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={method === m ? "button" : "button tinted"}
              onClick={() => setMethod(m)}
              disabled={busy}
            >
              {m === "CASH" ? "Cash" : m === "CARD" ? "Card" : "Transfer"}
            </button>
          ))}
        </div>
        {dueNow !== null ? (
          <small className="hint">
            {dueNow > 0
              ? `Takes ${dueNow.toFixed(2)} € now (already paid ${order.paidEur} €).`
              : refundDue > 0
                ? `Overpaid — hand back ${refundDue.toFixed(2)} € and record it via Record payment (refund).`
                : "Already paid in full — no new payment row."}
          </small>
        ) : null}
      </div>
      <div className="button-row">
        <button
          type="button"
          className="button"
          onClick={() => void submit()}
          disabled={busy}
        >
          {busy ? "Recording…" : "Record handover"}
        </button>
        <button type="button" className="button plain" onClick={onClose} disabled={busy}>
          Back
        </button>
      </div>
    </Modal>
  );
}

/**
 * G-3 (Orders.md §15.5): pick a recent register sale at the order's shop and
 * link it — the only road to COMPLETED. Server validates location match and
 * that the sale is not already linked; its errors surface verbatim.
 */
function LinkSaleModal({
  order,
  onClose,
  onLinked,
}: {
  order: CustomerOrder;
  onClose: () => void;
  onLinked: () => Promise<void>;
}) {
  const [sales, setSales] = useState<Sale[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    void api
      .listSales(order.location.id)
      .then((rows) => {
        if (stale) return;
        const sorted = [...rows].sort(
          (a, b) => new Date(b.soldAt).getTime() - new Date(a.soldAt).getTime(),
        );
        setSales(sorted.slice(0, 15));
      })
      .catch((err: unknown) => {
        if (!stale) {
          setError(err instanceof Error ? err.message : "Could not load sales.");
        }
      });
    return () => {
      stale = true;
    };
  }, [order.location.id]);

  const link = async (saleId: string) => {
    setBusyId(saleId);
    setError(null);
    try {
      await api.linkCustomerOrderSale(order.id, saleId);
      await onLinked();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not link the sale.");
      setBusyId(null);
    }
  };

  return (
    <Modal title={`Link a sale to ${order.code}`} onClose={onClose}>
      <p className="muted">
        Pick the register sale that fulfilled this order — the order then counts
        as completed. Most recent sales at {order.location.name} first.
      </p>
      {error ? <div className="alert error">{error}</div> : null}
      {!sales && !error ? <p className="muted">Loading…</p> : null}
      {sales && sales.length === 0 ? (
        <EmptyState
          title="No sales at this shop yet"
          message="Ring the order up first, then it links automatically."
        />
      ) : null}
      {sales?.map((sale) => (
        <div key={sale.id} className="day-shifts-row">
          <div className="day-shifts-row-head">
            <span>
              {new Date(sale.soldAt).toLocaleString(undefined, {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
              {" · "}
              {sale.soldBy.fullName}
            </span>
            <strong>{Number(sale.totalEur).toFixed(2)} €</strong>
          </div>
          <div className="button-row">
            <button
              type="button"
              className="button tinted"
              onClick={() => void link(sale.id)}
              disabled={busyId !== null}
            >
              {busyId === sale.id ? "Linking…" : "Link this sale"}
            </button>
          </div>
        </div>
      ))}
    </Modal>
  );
}
